import { z } from "zod";

/**
 * Enums shared by the database and every client. The Postgres enums in
 * packages/db are built from these arrays, so the two cannot drift.
 * Add an enum here in the phase that first needs it.
 */

export const PLATFORM_ADMIN_ROLES = ["superadmin", "support"] as const;
export const platformAdminRoleSchema = z.enum(PLATFORM_ADMIN_ROLES);
export type PlatformAdminRole = z.infer<typeof platformAdminRoleSchema>;

export const STAFF_ROLES = ["owner", "manager", "cashier", "waiter", "kitchen"] as const;
export const staffRoleSchema = z.enum(STAFF_ROLES);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const MEMBERSHIP_STATUSES = ["active", "disabled"] as const;
export const membershipStatusSchema = z.enum(MEMBERSHIP_STATUSES);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const RESTAURANT_STATUSES = ["onboarding", "active", "suspended", "archived"] as const;
export const restaurantStatusSchema = z.enum(RESTAURANT_STATUSES);
export type RestaurantStatus = z.infer<typeof restaurantStatusSchema>;

export const DEVICE_PLATFORMS = ["android", "web"] as const;
export const devicePlatformSchema = z.enum(DEVICE_PLATFORMS);
export type DevicePlatform = z.infer<typeof devicePlatformSchema>;

export const ACTOR_KINDS = ["staff", "admin", "guest", "system"] as const;
export const actorKindSchema = z.enum(ACTOR_KINDS);
export type ActorKind = z.infer<typeof actorKindSchema>;

/**
 * `webpush`: a browser subscription (staff web). `fcm`: an FCM registration
 * token of the Android app, sent to directly (ADR-027). `expo` is unused, kept
 * from the first schema because a Postgres enum value cannot be dropped.
 */
export const PUSH_PROVIDERS = ["expo", "webpush", "fcm"] as const;
export const pushProviderSchema = z.enum(PUSH_PROVIDERS);
export type PushProvider = z.infer<typeof pushProviderSchema>;

export const GST_MODES = ["none", "composition", "regular"] as const;
export const gstModeSchema = z.enum(GST_MODES);
export type GstMode = z.infer<typeof gstModeSchema>;

export const FILE_PURPOSES = [
	"item_image",
	"category_image",
	"logo",
	"cover",
	"gallery",
	"offer_banner",
	"menu_source",
	"site_asset",
	"invoice_pdf",
] as const;
export const filePurposeSchema = z.enum(FILE_PURPOSES);
export type FilePurpose = z.infer<typeof filePurposeSchema>;

export const FILE_STATUSES = ["pending", "ready", "deleted"] as const;
export const fileStatusSchema = z.enum(FILE_STATUSES);
export type FileStatus = z.infer<typeof fileStatusSchema>;

/** Diet marks on items and add-ons. Shown as the standard square-with-dot mark. */
export const DIETS = ["veg", "nonveg", "egg"] as const;
export const dietSchema = z.enum(DIETS);
export type Diet = z.infer<typeof dietSchema>;

/** `bills.kind`: the document a GST mode issues. See ordering-billing-gst.md. */
export const BILL_KINDS = ["tax_invoice", "bill_of_supply", "plain"] as const;
export const billKindSchema = z.enum(BILL_KINDS);
export type BillKind = z.infer<typeof billKindSchema>;

// Ordering. See docs/architecture/ordering-billing-gst.md and data-model.md, Ordering.

export const ORDER_TYPES = ["dine_in", "takeaway"] as const;
export const orderTypeSchema = z.enum(ORDER_TYPES);
export type OrderType = z.infer<typeof orderTypeSchema>;

export const ORDER_STATUSES = ["open", "billed", "completed", "cancelled"] as const;
export const orderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const BATCH_SOURCES = ["qr", "staff"] as const;
export const batchSourceSchema = z.enum(BATCH_SOURCES);
export type BatchSource = z.infer<typeof batchSourceSchema>;

export const BATCH_STATUSES = ["pending", "accepted", "rejected", "cancelled"] as const;
export const batchStatusSchema = z.enum(BATCH_STATUSES);
export type BatchStatus = z.infer<typeof batchStatusSchema>;

/**
 * `removed`: taken out by staff while accepting the batch, so it never
 * reached the kitchen or the bill. `cancelled`: cancelled after it was sent,
 * with a cancel KOT.
 */
export const ORDER_ITEM_STATUSES = ["active", "removed", "cancelled"] as const;
export const orderItemStatusSchema = z.enum(ORDER_ITEM_STATUSES);
export type OrderItemStatus = z.infer<typeof orderItemStatusSchema>;

export const KOT_KINDS = ["new", "cancel"] as const;
export const kotKindSchema = z.enum(KOT_KINDS);
export type KotKind = z.infer<typeof kotKindSchema>;

export const KOT_STATUSES = ["open", "done"] as const;
export const kotStatusSchema = z.enum(KOT_STATUSES);
export type KotStatus = z.infer<typeof kotStatusSchema>;

export const SERVICE_REQUEST_TYPES = ["waiter", "water", "bill", "other"] as const;
export const serviceRequestTypeSchema = z.enum(SERVICE_REQUEST_TYPES);
export type ServiceRequestType = z.infer<typeof serviceRequestTypeSchema>;

export const SERVICE_REQUEST_STATUSES = ["open", "acknowledged", "resolved"] as const;
export const serviceRequestStatusSchema = z.enum(SERVICE_REQUEST_STATUSES);
export type ServiceRequestStatus = z.infer<typeof serviceRequestStatusSchema>;

/**
 * How an order item was added (P9-T19): from the menu, a suggestion ("Goes
 * well with"), "Order again" on the status page, or by staff.
 */
export const ADD_SOURCES = ["menu", "suggestion", "reorder", "staff"] as const;
export const addSourceSchema = z.enum(ADD_SOURCES);
export type AddSource = z.infer<typeof addSourceSchema>;

/** Running numbers that restart each business date. */
export const DAILY_COUNTER_KINDS = ["order", "kot"] as const;
export type DailyCounterKind = (typeof DAILY_COUNTER_KINDS)[number];

// Billing. See docs/architecture/ordering-billing-gst.md and data-model.md, Billing.

/** `final`: issued and immutable. `void`: cancelled with a reason; the number is never reused. */
export const BILL_STATUSES = ["final", "void"] as const;
export const billStatusSchema = z.enum(BILL_STATUSES);
export type BillStatus = z.infer<typeof billStatusSchema>;

/** `razorpay` is reserved for the later integration; staff cannot record it by hand. */
export const PAYMENT_METHODS = ["cash", "upi", "card", "other", "razorpay"] as const;
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

/** `reversed`: recorded by mistake or given back, with a reason. It no longer counts. */
export const PAYMENT_STATUSES = ["confirmed", "reversed"] as const;
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

// Printing. See docs/architecture/printing.md and data-model.md, Printing.

/** How a device reaches the printer. `bridge` is Print Bridge on a PC; `system` the browser print dialog. */
export const PRINTER_CONNECTIONS = ["lan", "bluetooth", "usb", "bridge", "system"] as const;
export const printerConnectionSchema = z.enum(PRINTER_CONNECTIONS);
export type PrinterConnection = z.infer<typeof printerConnectionSchema>;

/** Text: the printer's font, CP437. Image: drawn by the device, any script. */
export const PRINT_MODES = ["text", "image"] as const;
export const printModeSchema = z.enum(PRINT_MODES);
export type PrinterMode = z.infer<typeof printModeSchema>;

export const PAPER_WIDTHS_MM = [58, 80] as const;

/** `print_jobs.doc_type`. A day close is a `report`. */
export const PRINT_DOC_TYPES = ["bill", "kot", "report", "test"] as const;
export const printDocTypeSchema = z.enum(PRINT_DOC_TYPES);
export type PrintDocType = z.infer<typeof printDocTypeSchema>;

/**
 * `queued`: waiting for a station. `claimed`: a station holds the lease.
 * `done`: printed. `failed`: a person decides (Retry or Mark as handled).
 * `cancelled`: marked as handled without printing.
 */
export const PRINT_JOB_STATUSES = ["queued", "claimed", "done", "failed", "cancelled"] as const;
export const printJobStatusSchema = z.enum(PRINT_JOB_STATUSES);
export type PrintJobStatus = z.infer<typeof printJobStatusSchema>;

/** What a station last saw of a printer. `unknown`: it gave no status answer, which is common. */
export const PRINTER_STATES = ["ready", "paper_out", "cover_open", "offline", "unknown"] as const;
export const printerStateSchema = z.enum(PRINTER_STATES);
export type PrinterState = z.infer<typeof printerStateSchema>;

/**
 * Why a job failed, shown to staff in plain words (printing.md, Failure).
 * `may_have_printed`: its lease ran out after the device started sending, so
 * nobody knows; never retried by itself.
 */
export const PRINT_FAILURE_REASONS = [
	"paper_out",
	"cover_open",
	"offline",
	"unreachable",
	"partial_write",
	"may_have_printed",
	"no_rasteriser",
	"other",
] as const;
export const printFailureReasonSchema = z.enum(PRINT_FAILURE_REASONS);
export type PrintFailureReason = z.infer<typeof printFailureReasonSchema>;

// Offers. See docs/architecture/data-model.md, Promotions, and storefront-sites.md, Selling more.

/**
 * `item_discount`: a lower price on some items. `bill_discount`: off the
 * bill, usually above a minimum order. `coupon`: a bill discount that needs
 * its code.
 */
export const OFFER_KINDS = ["item_discount", "bill_discount", "coupon"] as const;
export const offerKindSchema = z.enum(OFFER_KINDS);
export type OfferKind = z.infer<typeof offerKindSchema>;

/**
 * `percent`: `value` in basis points. `flat`: `value` paise off (each unit for
 * an item offer). `fixed_price`: the item costs `value` paise (item offers only).
 */
export const OFFER_DISCOUNT_TYPES = ["percent", "flat", "fixed_price"] as const;
export const offerDiscountTypeSchema = z.enum(OFFER_DISCOUNT_TYPES);
export type OfferDiscountType = z.infer<typeof offerDiscountTypeSchema>;

/** What an item offer applies to. Bill offers and coupons are always `all`. */
export const OFFER_APPLIES_TO = ["all", "items", "categories"] as const;
export const offerAppliesToSchema = z.enum(OFFER_APPLIES_TO);
export type OfferAppliesTo = z.infer<typeof offerAppliesToSchema>;
